// ============= Homepage Healing Sanctuary — soft, calming card =============

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMembership } from "@/hooks/useMembership";
import { sanctuaryEntryPath } from "@/lib/sanctuary-access";
import roomArt from "@/assets/sanctuary/room.jpg";

/**
 * Homepage Healing Sanctuary: a gentle invitation into the member-gated
 * cosy room, shown where the Halloween special used to sit.
 */
export function HomeSanctuary() {
  const membership = useMembership();
  const entryPath = sanctuaryEntryPath(membership);
  const canEnter = entryPath === "/sanctuary";

  return (
    <section
      className="mx-auto w-full max-w-5xl rounded-3xl border border-gold/20 bg-[var(--qa-lavender-tint)] p-4 sm:p-6 lg:p-8"
      aria-labelledby="home-sanctuary-title"
    >
      <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-stretch">
        <Link
          to="/sanctuary"
          aria-label={canEnter ? "Enter the Healing Sanctuary" : "Preview the Healing Sanctuary — members only"}
          className="relative block h-auto min-h-32 w-full shrink-0 overflow-hidden rounded-2xl border border-gold/20 sm:w-[240px] lg:w-[300px]"
        >
          <img
            src={roomArt}
            alt="The Healing Sanctuary — a cosy, candlelit room with a crackling fireplace, a soft armchair, a journal and a moonlit window"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </Link>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center sm:items-start sm:text-start">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--gold-deep)]">
            A quiet place to rest
          </p>
          <h2 id="home-sanctuary-title" className="font-display text-2xl font-semibold sm:text-3xl">
            The Healing Sanctuary{" "}
            <span className="ms-1 inline-block rounded-full border border-gold/40 bg-gold-light/40 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--gold-deep)]">
              New
            </span>
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            A cosy illustrated room with a crackling fire, a soft armchair, a letting-go ritual and a moonlit
            window. Tap what feels right — everything stays private.
          </p>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
            <Button asChild variant="gold" size="sm">
              {canEnter ? (
                <Link to="/sanctuary">
                  Enter the Sanctuary <ArrowRight />
                </Link>
              ) : (
                <Link to="/pricing">
                  Members only — join to enter <ArrowRight />
                </Link>
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
